//@flow
import Model from '../../Utils/Model/model';
import addDays from 'date-fns/add_days';
import subDays from 'date-fns/sub_days';
import UrlData from '../../Utils/url-data';

import type { QuoteTotalsObject } from './quoteTotalsModel';
import type { QuoteItemObject } from './quoteItemModel';

type ProcessorType = 'amazon'|'stripe';

export type QuoteObject = {
  id:string,
  shipping_address_id:?string,
  shipping_rate_id:?string,
  gift_message?:{
    message:string,
  },
  coupon_code:?{
    name:?string,
    amount:?string,
  },
  payment:{
    card_id:string,
    processor_type:?ProcessorType,
    method?:?string,
  },
  reserve_expiration:?number,
  shipping_rates:{[string]:{
    price:string,
    title:string,
  }},
  customer_group:string,
  is_guest:boolean,
  ss_order_date:?number,
  ss_locked:boolean,
  subscribe:boolean,
  ss_interval:?string,
  ss_first_interval:?string,
  is_virtual:boolean,
  totals?:QuoteTotalsObject,
  items:{ [string]:QuoteItemObject },
  new_membership_plan_code:string,
  available_payment_methods:string[],
  ss_restricted:boolean,
  ip_country:?string,
};

export default class QuoteModel extends Model<QuoteObject> {
  getId():?string {
    return this.attributes.id || undefined;
  }

  getShippingAddressId():?string {
    return this.attributes.shipping_address_id || undefined;
  }

  getPersonalizedMessage():?string {
    return this.attributes.gift_message && this.attributes.gift_message.message;
  }

  getShippingRateId():?string {
    const shippingRateId = this.attributes.shipping_rate_id;

    if (typeof shippingRateId !== 'string') return undefined;

    return shippingRateId;
  }

  getCustomerGroup():?string {
    return this.attributes.customer_group;
  }

  isMsrp():boolean {
    return this.getCustomerGroup() === 'MSRP';
  }

  isVirtual():boolean {
    return this.attributes.is_virtual || false;
  }

  getShippingRates():?{[any]:{price:string, title:string}} {
    let shippingRates = this.attributes.shipping_rates;
    if (!shippingRates) return;

    shippingRates = Object.assign({}, shippingRates);

    Object.keys(shippingRates).forEach(rate => {
      if (!this._isAllowedShippingRate(rate)) delete shippingRates[rate];
    });
    return shippingRates;
  }

  _isAllowedShippingRate(rate:string):boolean {
    return (rate === 'checkout_checkout') ||
    (rate === 'checkout_express');
  }

  getProcessorType():?ProcessorType {
    return this.attributes.payment && this.attributes.payment.processor_type || undefined;
  }

  getPaymentId():?string {
    const payment = this.attributes.payment;
    if (!payment) return undefined;

    let id = payment.card_id;
    if (typeof id === 'number') id = id.toString();
    if (typeof id !== 'string') return undefined;

    return id;
  }

  getPaymentMethod():?string {
    const payment = this.attributes.payment;
    if (!payment) return undefined;

    return payment.method;
  }

  getSSInterval():?string {
    return this.attributes.ss_interval;
  }

  getSSFirstInterval():?string {
    return this.attributes.ss_first_interval;
  }

  // Subscribe and save order date
  getSSOrderDate():?Date {
    const ssOrderDate = this.attributes.ss_order_date;
    return ssOrderDate ? new Date(ssOrderDate) : undefined;
  }

  // Estimated order date + 2 days
  getSSShippingDate():?Date {
    const date = this.getSSOrderDate();
    return date && addDays(date, 2);
  }

  // Estimated order date - 3 days
  getSSLastDateToUpdateOrder():?Date {
    const date = this.getSSOrderDate();
    return date && subDays(date, 3);
  }

  // Estimated order date - 2 days
  getSSProcessingDate():?Date {
    const date = this.getSSOrderDate();
    return date && subDays(date, 2);
  }

  isActiveSSQuote():boolean {
    return Boolean(this.attributes.ss_order_date && !this.attributes.ss_locked);
  }

  getSSIntervalText():string {
    switch (this.getSSInterval()) {
      case 'bimonthly':
        return 'every other month';
      case 'monthly':
      default:
        return 'every month';
    }
  }

  getNewMembershipPlanCode():string {
    return this.attributes.new_membership_plan_code;
  }

  isSSLocked():boolean {
    return this.attributes.ss_locked;
  }

  isReservationExpired():boolean {
    const expiration = this.attributes.reserve_expiration;
    if (!expiration) return false;

    return new Date(expiration) < QuoteModel._getCurrentDate();
  }

  isReserved():boolean {
    const expiration = this.attributes.reserve_expiration;
    if (!expiration) return false;

    return new Date(expiration) > QuoteModel._getCurrentDate();
  }

  isCouponCodeApplied(code:string):boolean {
    const couponCode = this.attributes.coupon_code;
    const name = couponCode && couponCode.name;

    return !!name && name.includes(code);
  }

  isGuest():boolean {
    return this.attributes.is_guest;
  }

  isSubscription():boolean {
    return this.attributes.subscribe;
  }

  getValidPaymentMethods():string[] {
    return this.attributes.available_payment_methods || [];
  }

  isAmazonPayValid():boolean {
    return this.getValidPaymentMethods().includes('amazon');
  }

  isUsingAmazon() {
    return this.getProcessorType() === 'amazon';
  }

  getCreditPaymentMethod():string {
    // Temporal change to manually force authorize payments
    if (UrlData.parseUrlForKey('nb_pay_meth') === 'authorize') return 'authnetcim';

    const methods = this.getValidPaymentMethods();

    if (!methods.includes('cryozonic_stripe') && !methods.includes('free') && methods.includes('authnetcim')) {
      return 'authnetcim';
    }

    return ('cryozonic_stripe');
  }

  isSSRestricted():boolean {
    return this.attributes.ss_restricted;
  }

  getIpCountry():string {
    // Assume US by default
    return this.attributes.ip_country || 'US';
  }

  static _getCurrentDate():Date {
    return new Date();
  }
}
